<template>
    <QueryLoaderGuard
        :is-pending="isLoading"
        :is-error="isError"
    >
        <div class="condition-status-historical-trends-chart">
            <div
                v-if="!showChart"
                class="condition-status-historical-trends-chart__empty-state"
            >
                <pbl-icon name="check2-square" />
                <span>{{ t('generators.conditionStatus.content.historicalTrends.chart.emptyState') }}</span>
            </div>
            <template v-else-if="showChart">
                <div
                    v-if="!dimensions.length"
                    class="condition-status-historical-trends-chart__empty-state"
                >
                    <pbl-icon name="x-circle" />
                    <span>{{ t('generators.conditionStatus.content.historicalTrends.chart.noData') }}</span>
                </div>
                <v-chart
                    v-else
                    ref="chartRef"
                    class="condition-status-historical-trends-chart__chart"
                    :theme="themeName"
                    :option="option"
                    autoresize
                />
            </template>
        </div>
    </QueryLoaderGuard>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, nextTick, watch } from 'vue'
import { use, color } from 'echarts/core'
import { EChartsOption } from 'echarts'
import { LineChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import {
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import { compareAsc, format } from 'date-fns'
import { useI18n } from 'vue-i18n'
import { useConstantsQuery } from '@/api/queries/general/useConstantsQuery'
import { ECHARTS_COLOR_PALETTE } from '@/utils/echarts-utils'
import { StatusTypes } from '@/configuration/status/condition-status'
import { DATE_FORMAT } from '@/utils/date-utils'
import { CHART_ICON } from '@/utils/chart-utils'
import { LanguageType } from '@/configuration/language/language-types'
import { ColorType } from '@/configuration/color/color-type'
import { useGeneratorHistoricalQuery } from '@/api/queries/generators/useGeneratorHistoricalQuery'
import { useGeneratorId } from '@/composables/useGeneratorId'
import { useTheme } from '@/composables/useTheme'
import {
    ECHARTS_THEME_DARK,
    ECHARTS_THEME_LIGHT,
    getChartLabelColor,
    getChartToolboxInactiveColor,
} from '@/utils/echarts-theme'

const REFERENCE_MEASUREMENT_KEY = 'ref. meas' // Used to identify reference measurement lines

use([
    TitleComponent,
    LineChart,
    CanvasRenderer,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
])

const { t, locale } = useI18n()
const { isDarkMode } = useTheme()

const generatorId = useGeneratorId()

const props = defineProps<{
    measurementType?: number
    measurementPoint?: number
    startDate?: string
    endDate?: string
    hideToolbox?: boolean
    forceLightTheme?: boolean
}>()

const { measurementType, measurementPoint, startDate, endDate, hideToolbox, forceLightTheme } = toRefs(props)
const themeName = computed<typeof ECHARTS_THEME_LIGHT | typeof ECHARTS_THEME_DARK>(() => {
    if (forceLightTheme.value) {
        return ECHARTS_THEME_LIGHT
    }

    return isDarkMode.value ? ECHARTS_THEME_DARK : ECHARTS_THEME_LIGHT
})

const isChartDarkMode = computed(() => themeName.value === ECHARTS_THEME_DARK)

const chartRef = ref<InstanceType<typeof VChart> | null>(null)
const showTitle = ref(false)

const emit = defineEmits<{
    'update:hasData': [value: boolean]
}>()

const isTimeframeValid = computed(() => {
    if (!startDate.value || !endDate.value) {
        return false
    }

    return compareAsc(new Date(startDate.value), new Date(endDate.value)) === -1
})

const showChart = computed(() => isTimeframeValid.value && !!measurementType.value && !!measurementPoint.value)

const languageType = computed(() => (LanguageType[locale.value as unknown as LanguageType] as unknown as number) ?? LanguageType['en-GB'])

const { data: constants } = useConstantsQuery()
const { data, isLoading, isError } = useGeneratorHistoricalQuery(
    generatorId,
    measurementType,
    measurementPoint,
    languageType,
    startDate,
    endDate,
)

const dimensions = computed(() => legend.value ? Object.keys(legend.value) : [])
const statuses = computed(() => data.value?.statuses ?? [])
const unitsOfMeasure = computed(() => data.value?.unitsOfMeasure ?? [])
const legend = computed(() => data.value?.legend ?? {})

const measurementName = computed(() => {
    const foundMeasurementType = constants.value?.generatorHistoricalTrends?.options?.find((type) => type.id === measurementType.value)
    const foundMeasurementPoint = foundMeasurementType?.options?.find((point) => point.id === measurementPoint.value)

    if (!foundMeasurementType?.translationKey || !foundMeasurementPoint?.translationKey) {
        return ''
    }

    return `${t(foundMeasurementType.translationKey)}_${t(foundMeasurementPoint.translationKey)}`
})

const saveAsImageTitle = computed(() => `${t('generators.conditionStatus.content.historicalTrends.title')} - ${measurementName.value}`)

const option = computed(() => {
    const options: EChartsOption = {
        title: {
            show: showTitle.value,
            text: saveAsImageTitle.value,
            top: 5,
            left: '40%',
            textStyle: {
                fontSize: 16,
                fontWeight: 500,
            },
        },
        grid: {
            top: 120,
            left: 5,
            right: 50,
            bottom: 5,
            containLabel: true,
        },
        legend: {
            selected: legend.value,
            top: 30,
            width: '90%',
        },
        toolbox: {
            show: !hideToolbox.value,
            orient: 'vertical',
            itemSize: 16,
            itemGap: 14,
            showTitle: true,
            feature: {
                dataZoom: {
                    icon: {
                        zoom: CHART_ICON.ZOOM,
                        back: CHART_ICON.BACK,
                    },
                    title: {
                        zoom: t('chart.action.zoom'),
                        back: t('chart.action.back'),
                    },
                },
                mySaveAsImage: {
                    title: t('chart.action.saveAsImage'),
                    icon: CHART_ICON.SAVE,
                    onclick: async () => {
                        showTitle.value = true
                        await nextTick()

                        const dataURL = chartRef.value?.chart?.getDataURL({
                            type: 'png',
                            pixelRatio: 2,
                            backgroundColor: '#fff',
                            excludeComponents: ['toolbox'],
                        })

                        if (!dataURL) {
                            await nextTick()
                            showTitle.value = false

                            return
                        }

                        const link = document.createElement('a')
                        link.href = dataURL
                        link.download = saveAsImageTitle.value
                        link.click()
                        link.remove()

                        await nextTick()
                        showTitle.value = false
                    },
                },
            },
            iconStyle: {
                color: getChartToolboxInactiveColor(isChartDarkMode.value),
                borderColor: 'none',
            },
            emphasis: {
                iconStyle: {
                    color: ColorType.Primary,
                    borderColor: 'none',
                },
            },
        },
        tooltip: {
            trigger: 'axis',
            formatter: (args) => {
                if (!Array.isArray(args)) {
                    return ''
                }

                const axisTimestamp = (args[0].value as Record<string, unknown>).timestamp as string
                const axisValueLabel = format(new Date(axisTimestamp), DATE_FORMAT)

                const wrapperElement = document.createElement('div')
                wrapperElement.classList.add('wrapper')
                document.body.appendChild(wrapperElement)

                const axisLabelElement = document.createElement('div')
                axisLabelElement.classList.add('wrapper__axis-label')
                axisLabelElement.textContent = axisValueLabel
                wrapperElement.appendChild(axisLabelElement)

                args.forEach((element) => {
                    const seriesName = element.seriesName ?? ''
                    const status = StatusTypes[statuses.value?.[element.dataIndex]?.[seriesName]]
                    const unitOfMeasure =
                        constants.value?.unitOfMeasureTypes?.[
                            unitsOfMeasure.value?.[element.dataIndex]?.[seriesName]
                        ] ?? ''

                    const contentItemElement = document.createElement('div')
                    contentItemElement.classList.add('wrapper__content-item')
                    wrapperElement.appendChild(contentItemElement)

                    const colorCircleElement = document.createElement('span')
                    colorCircleElement.classList.add('wrapper__content-item__color-circle')
                    colorCircleElement.style.backgroundColor = element.color?.toString() ?? '#333'
                    contentItemElement.appendChild(colorCircleElement)

                    const seriesNameElement = document.createElement('span')
                    seriesNameElement.classList.add('wrapper__content-item__series-name')
                    seriesNameElement.textContent = seriesName
                    contentItemElement.appendChild(seriesNameElement)

                    const floatRightContainerElement = document.createElement('div')
                    floatRightContainerElement.classList.add('wrapper__content-item__float-right-container')
                    contentItemElement.appendChild(floatRightContainerElement)

                    const valueElement = document.createElement('span')
                    valueElement.classList.add('wrapper__content-item__float-right-container__value')
                    valueElement.textContent = `${(element.value as Record<string, unknown>)[seriesName] ?? '-'}`
                    floatRightContainerElement.appendChild(valueElement)

                    const unitOfMeasureElement = document.createElement('span')
                    unitOfMeasureElement.classList.add('wrapper__content-item__float-right-container__unit-of-measure')
                    unitOfMeasureElement.textContent = unitOfMeasure
                    floatRightContainerElement.appendChild(unitOfMeasureElement)

                    const statusElement = document.createElement('span')
                    statusElement.classList.add('wrapper__content-item__float-right-container__status-label')
                    if (status) {
                        statusElement.textContent = t(status.labelKey)
                        statusElement.style.backgroundColor = status.color
                    }
                    floatRightContainerElement.appendChild(statusElement)
                })

                return wrapperElement
            },
        },
        dataset: {
            source: data.value?.dataPoints ?? [],
            dimensions: ['timestamp', ...dimensions.value],
        },
        xAxis: {
            type: 'time',
            axisLabel: {
                formatter: '{dd}/{MM}/{yyyy}',
            }
        },
        yAxis: {
            scale: true,
        },
        series: [],
    }

    let colorIndex = 0
    const baseColorMap = new Map<string, string>()

    dimensions.value.forEach((dimension) => {
        const series = options.series

        if (!Array.isArray(series)) {
            return
        }

        const randomColor = color.random()
        const isReferenceMeasurement = dimension.includes(REFERENCE_MEASUREMENT_KEY)
        
        const refIndex = dimension.indexOf(REFERENCE_MEASUREMENT_KEY)
        const baseDimension = refIndex >= 0 ? dimension.slice(0, refIndex).trim() : dimension.trim()
        
        let lineColor: string

        if (baseColorMap.has(baseDimension)) {
            lineColor = baseColorMap.get(baseDimension)!
        } else {
            lineColor = ECHARTS_COLOR_PALETTE[colorIndex] ?? randomColor
            baseColorMap.set(baseDimension, lineColor)
            
            colorIndex++
        }

        const lineStyleType: 'solid' | 'dashed' = isReferenceMeasurement ? 'dashed' : 'solid'

        series.push({
            name: dimension,
            type: 'line',
            lineStyle: {
                type: lineStyleType,
            },
            encode: {
                x: 'timestamp',
                y: dimension,
            },
            color: lineColor,
            label: {
                show: true,
                position: 'top',
                color: getChartLabelColor(isChartDarkMode.value),
                fontWeight: 'bold',
            },
            symbolSize: 8,
        })
    })

    return options
})

watch([data, isLoading, isError], ([newDataPoints]) => {
    emit('update:hasData', (newDataPoints?.dataPoints?.length ?? -1) > 0)
})
</script>

<style scoped lang="scss">
.condition-status-historical-trends-chart {
    &__selector {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 1em;

        &__field {
            min-width: 300px;
        }
    }

    &__empty-state {
        height: 400px;

        display: flex;
        flex-direction: column;

        align-items: center;
        justify-content: center;
        gap: $spacing-s;

        color: $pbl-text-muted;

        pbl-icon {
            --size: 2rem;
        }

        span {
            font-size: 1rem;
        }
    }

    &__chart {
        width: 100%;
        height: 400px;
    }
}
</style>

<style lang="scss">
.wrapper {
    margin: 0;
    line-height: 1;

    &__axis-label {
        font-size: $default-font-size;
        color: $pbl-text;
    }

    &__content-item {
        margin: 10px 0 0;
        line-height: 1;

        &__color-circle {
            display: inline-block;
            margin-right: 4px;
            border-radius: 10px;
            width: 10px;
            height: 10px;
        }

        &__series-name {
            font-size: $default-font-size;
            color: $pbl-text !important;
            font-weight: 400;
            margin-left: 2px;
        }

        &__float-right-container {
            float: right;
            margin-left: 20px;
            display: flex;

            &__value,
            &__unit-of-measure {
                font-size: $default-font-size;
                color: $pbl-text-muted;
            }

            &__unit-of-measure {
                margin-left: 4px;
            }

            &__status-label {
                margin-left: 20px;
                min-width: 50px;
                padding: 4px 6px;
                color: #ffffff;
                font-size: 10px;
                font-weight: 400;
                border-radius: 2px;
                text-align: center;
            }
        }
    }
}
</style>
